/** Portal-rendered overlays: modal, drawer, anchored menu. */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./Icon";
import { Button } from "./primitives";

/**
 * Escape closes the topmost overlay only.
 *
 * A drawer that opens a modal must not have both dismissed by one keypress, so
 * open overlays push onto a stack and only the last one listens.
 */
const stack: symbol[] = [];

function useTopmostEscape(onClose: () => void) {
  useEffect(() => {
    const id = Symbol("overlay");
    stack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (stack[stack.length - 1] !== id) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const i = stack.indexOf(id);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [onClose]);
}

/** Keeps the page behind an overlay from scrolling under it. */
function useScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
}

/* ---------------------------------------------------------------------- *
 * Modal
 * ---------------------------------------------------------------------- */
export function Modal({
  title,
  description,
  footer,
  wide,
  onClose,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  onClose: () => void;
  children?: ReactNode;
}) {
  useTopmostEscape(onClose);
  useScrollLock();
  const bodyRef = useRef<HTMLDivElement>(null);

  // Focus the first control so the dialog is usable from the keyboard the
  // moment it opens.
  useEffect(() => {
    const el = bodyRef.current?.querySelector<HTMLElement>(
      "input, select, textarea, button",
    );
    el?.focus();
  }, []);

  return createPortal(
    <>
      <div className="scrim" />
      <div
        className="modal-wrap"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="modal" role="dialog" aria-modal="true" style={wide ? { maxWidth: 840 } : undefined}>
          <header className="modal__hd">
            <h3>{title}</h3>
            {description && <p>{description}</p>}
          </header>
          <div className="modal__bd" ref={bodyRef}>
            {children}
          </div>
          {footer && <footer className="modal__ft">{footer}</footer>}
        </div>
      </div>
    </>,
    document.body,
  );
}

/* ---------------------------------------------------------------------- *
 * Drawer
 * ---------------------------------------------------------------------- */
export function Drawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useTopmostEscape(onClose);
  useScrollLock();
  return createPortal(
    <>
      <div className="scrim" onMouseDown={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true">
        {children}
      </aside>
    </>,
    document.body,
  );
}

export function DrawerHeader({
  thumb,
  title,
  subtitle,
  badges,
  onClose,
}: {
  thumb?: string | null;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  onClose: () => void;
}) {
  return (
    <header className="drawer__hd">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        {thumb && (
          <img
            src={thumb}
            alt=""
            style={{
              width: 112,
              height: 63,
              objectFit: "cover",
              borderRadius: "var(--r-sm)",
              border: "1px solid var(--border)",
              flex: "none",
            }}
          />
        )}
        <div className="grow">
          <h2 style={{ fontSize: "var(--t-lg)", lineHeight: 1.35 }}>{title}</h2>
          {subtitle && (
            <div className="muted" style={{ fontSize: "var(--t-md)", marginTop: 2 }}>
              {subtitle}
            </div>
          )}
          {badges && (
            <div className="row" style={{ gap: 6, marginTop: 10 }}>
              {badges}
            </div>
          )}
        </div>
        <Button variant="ghost" iconOnly icon="close" title="Close (Esc)" onClick={onClose} />
      </div>
    </header>
  );
}

export function DrawerTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; count?: number }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <nav className="drawer__tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`drawer__tab ${active === t.id ? "is-active" : ""}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count != null && <span className="badge">{t.count}</span>}
        </button>
      ))}
    </nav>
  );
}

/* ---------------------------------------------------------------------- *
 * Anchored menu (row "…" actions)
 * ---------------------------------------------------------------------- */
export type MenuEntry =
  | "-"
  | { header: string }
  | { label: string; icon?: IconName; danger?: boolean; onClick: () => void };

export function Menu({
  anchor,
  items,
  onClose,
}: {
  anchor: DOMRect;
  items: MenuEntry[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useTopmostEscape(onClose);

  // Measure first, then place: a menu near the bottom of the window flips
  // above its anchor rather than running off the screen.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    const left = Math.max(8, Math.min(anchor.right - w, window.innerWidth - w - 8));
    const top =
      anchor.bottom + h + 8 > window.innerHeight
        ? Math.max(8, anchor.top - h - 4)
        : anchor.bottom + 4;
    setPos({ left, top });
  }, [anchor]);

  useEffect(() => {
    const close = () => onClose();
    // `true` for scroll: a scrolling table body doesn't bubble, and a menu
    // pinned to a row that moved is worse than no menu.
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [onClose]);

  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 79 }}
        onMouseDown={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={ref}
        className="menu"
        role="menu"
        style={{
          left: pos?.left ?? -9999,
          top: pos?.top ?? -9999,
          visibility: pos ? "visible" : "hidden",
        }}
      >
        {items.map((it, i) => {
          if (it === "-") return <div key={i} className="menu__sep" />;
          if ("header" in it)
            return (
              <div key={i} className="menu__hd">
                {it.header}
              </div>
            );
          return (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={`menu__item ${it.danger ? "menu__item--danger" : ""}`}
              onClick={() => {
                onClose();
                it.onClick();
              }}
            >
              <span className="ic">{it.icon && <Icon name={it.icon} size={15} />}</span>
              {it.label}
            </button>
          );
        })}
      </div>
    </>,
    document.body,
  );
}

/** Opens a Menu anchored to whatever element was clicked. */
export function useMenu() {
  const [state, setState] = useState<{ anchor: DOMRect; items: MenuEntry[] } | null>(null);
  const close = useCallback(() => setState(null), []);
  const open = useCallback((el: HTMLElement, items: MenuEntry[]) => {
    setState({ anchor: el.getBoundingClientRect(), items });
  }, []);
  const node = state ? <Menu anchor={state.anchor} items={state.items} onClose={close} /> : null;
  return { open, close, node };
}
